import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  User, Phone, Mail, MapPin, Building2, Users, Heart, Shield,
  Award, Clock, Globe, ChevronDown, ChevronUp, Star, Briefcase,
  Calendar, Home, MessageCircle, ExternalLink, ArrowLeft, AlertCircle
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import MobileFooter from './MobileFooter';
import baseurl from '../Baseurl/baseurl';
import { Button, Card, Eyebrow, Heading, StatusBadge, Badge, Avatar, Loader, EmptyState, Container } from '../ui';

const MemberDetailsPage = () => {
  const { id } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    personalInfo: true,
    contactInfo: false,
    familyInfo: false,
    businesses: true,
    culturalInfo: false
  });

  useEffect(() => {
    const fetchMember = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${baseurl}/api/member/${id}`);
        const data = await res.json();
        if (data.success) {
          setMember(data.data);
        } else {
          setError('Failed to load member');
        }
      } catch (e) {
        setError('Failed to load member');
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
  }, [id]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fixed profile image URL construction
  const getProfileImageUrl = () => {
    if (!member?.profile_image) return null;

    let imagePath = member.profile_image;

    // Remove any leading slashes to avoid double slashes in URL
    if (imagePath.startsWith('/')) {
      imagePath = imagePath.substring(1);
    }

    // Construct the full URL
    return `${baseurl}/${imagePath}`;
  };

  const profileImg = getProfileImageUrl();
  const family = member?.MemberFamily || member?.family;
  const businesses = member?.BusinessProfiles || member?.businesses || [];

  // Helper function to format children names
  const formatChildrenNames = (value) => {
    if (!value) return '';
    try {
      if (Array.isArray(value)) {
        return value.filter(Boolean).join(', ');
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || trimmed.startsWith('{')) {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.filter(Boolean).join(', ');
          }
        }
        return trimmed
          .replace(/^[\[\"]+|[\]\"]+$/g, '')
          .split(',')
          .map((s) => s.replace(/^[\"\s]+|[\"\s]+$/g, ''))
          .filter(Boolean)
          .join(', ');
      }
      return String(value);
    } catch (e) {
      return String(value)
        .replace(/[\[\]"]+/g, '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .join(', ');
    }
  };

  // Collapsible Card Component
  const CollapsibleCard = ({ title, isExpanded, onToggle, icon: Icon, children, gradient = false }) => {
    return (
      <Card padding="none" className="overflow-hidden transition-all duration-300">
        <button
          onClick={onToggle}
          className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between transition-all duration-300 hover:bg-primary/5"
        >
          <div className="flex items-center gap-3 sm:gap-4">
            {Icon && (
              <div className="rounded-md bg-primary/10 p-2 text-primary">
                <Icon className="w-4 h-4" />
              </div>
            )}
            <h3 className="font-display font-semibold text-base sm:text-lg text-ink tracking-tight">{title}</h3>
          </div>
          <div className={`p-1.5 sm:p-2 rounded-full transition-all duration-300 ${isExpanded ? 'bg-primary/15 rotate-180' : 'bg-line/8'}`}>
            <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-muted" />
          </div>
        </button>
        {isExpanded && (
          <div className="border-t border-line/12">
            <div className="p-4 sm:p-6">
              {children}
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-bg pb-20 lg:pb-0">
      <Header />

      <Container width="wide" className="py-4 sm:py-6">
        {/* Back Button */}
        <div className="mb-4 sm:mb-6">
          <Link
            to="/members"
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-muted hover:text-ink transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Members</span>
          </Link>
        </div>

        {loading && (
          <Loader label="Loading member details…" />
        )}

        {error && (
          <Card tone="dark" className="text-center mb-8 border-danger/25 bg-danger/10">
            <AlertCircle className="w-10 h-10 text-danger mx-auto mb-4" />
            <p className="text-danger font-medium text-lg">{error}</p>
          </Card>
        )}

        {!loading && !error && member && (
          <>
            {/* Enhanced Hero Section - ALL LEFT ALIGNED */}
            <Card padding="none" className="overflow-hidden mb-6 sm:mb-8">
              {/* Mobile Layout - Left Aligned */}
              <div className="block sm:hidden">
                {/* Dark band */}
                <div className="on-dark bg-navy h-20 relative">
                  <div className="absolute inset-0 bg-navy/60"></div>
                </div>

                {/* Profile Content - Left Aligned */}
                <div className="relative px-4 pb-6 -mt-8">
                  {/* Profile Image - Left Aligned */}
                  <div className="flex justify-start mb-4">
                    <div className="relative">
                      <div className="w-24 h-24 bg-surface rounded-2xl p-1 shadow-card border border-line/15">
                        <div className="w-full h-full rounded-xl overflow-hidden bg-primary/10 flex items-center justify-center">
                          {profileImg && !imageError ? (
                            <img
                              src={profileImg}
                              alt={member.first_name}
                              className="w-full h-full object-cover"
                              onError={() => setImageError(true)}
                            />
                          ) : (
                            <div className="flex items-center justify-center">
                              <User className="w-10 h-10 text-primary" />
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Status indicator */}
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full border-2 border-surface flex items-center justify-center">
                        <Shield className="w-3 h-3 text-onprimary" />
                      </div>
                    </div>
                  </div>

                  {/* Member Info - Left Aligned */}
                  <div className="text-left">
                    <Heading variant="title" as="h1" className="mb-3">
                      {member.first_name} {member.last_name}
                    </Heading>
                    <div className="flex flex-col gap-2 items-start">
                      {member.email && (
                        <Badge tone="primary">{member.email}</Badge>
                      )}
                      {member.contact_no && (
                        <Badge tone="info">{member.contact_no}</Badge>
                      )}
                      {(member.kootam || member.kovil) && (
                        <Badge tone="accent">{member.kootam || member.kovil}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop/Tablet Layout - Left Aligned */}
              <div className="hidden sm:block">
                <div className="on-dark bg-navy h-32 relative">
                  <div className="absolute inset-0 bg-navy/60"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex flex-row items-end gap-6">
                      {/* Profile Image */}
                      <div className="relative">
                        <div className="w-24 h-24 md:w-28 md:h-28 bg-surface rounded-2xl sm:rounded-3xl p-1 shadow-card border border-line/15">
                          <div className="w-full h-full rounded-xl sm:rounded-2xl overflow-hidden bg-primary/10 flex items-center justify-center">
                            {profileImg && !imageError ? (
                              <img
                                src={profileImg}
                                alt={member.first_name}
                                className="w-full h-full object-cover"
                                onError={() => setImageError(true)}
                              />
                            ) : (
                              <div className="flex items-center justify-center">
                                <User className="w-12 h-12 text-primary" />
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Status indicator */}
                        <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-full border-4 border-surface flex items-center justify-center">
                          <Shield className="w-4 h-4 text-onprimary" />
                        </div>
                      </div>

                      {/* Member Info - Left Aligned */}
                      <div className="flex-1 text-left min-w-0">
                        <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-ink">
                          {member.first_name} {member.last_name}
                        </h1>
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          {member.email && (
                            <span className="px-3 py-1 bg-surface/15 rounded-full text-text font-medium text-sm border border-line/25">
                              {member.email}
                            </span>
                          )}
                          {member.contact_no && (
                            <span className="px-3 py-1 bg-surface/15 rounded-full text-text font-medium text-sm border border-line/25">
                              {member.contact_no}
                            </span>
                          )}
                          {(member.kootam || member.kovil) && (
                            <span className="px-3 py-1 bg-surface/15 rounded-full text-text font-medium text-sm border border-line/25">
                              {member.kootam || member.kovil}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Content Grid - Left Aligned */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                {/* Personal Information - Left Aligned */}
                <CollapsibleCard
                  title="Personal Information"
                  isExpanded={expandedSections.personalInfo}
                  onToggle={() => toggleSection('personalInfo')}
                  icon={User}
                  gradient={true}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="p-2 bg-info/10 rounded-lg flex-shrink-0">
                          <User className="w-4 h-4 text-info" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block text-sm sm:text-base">Full Name</span>
                          <p className="text-muted text-sm sm:text-base text-left">{member.first_name} {member.last_name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                          <Calendar className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Date of Birth</span>
                          <p className="text-muted text-left">{member.dob || 'Not available'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-accent/15 rounded-lg flex-shrink-0">
                          <Users className="w-4 h-4 text-accent" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Gender</span>
                          <p className="text-muted text-left">{member.gender || 'Not available'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-danger/10 rounded-lg flex-shrink-0">
                          <Heart className="w-4 h-4 text-danger" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Marital Status</span>
                          <p className="text-muted text-left">{member.marital_status || 'Not available'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-danger/10 rounded-lg flex-shrink-0">
                          <Shield className="w-4 h-4 text-danger" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Blood Group</span>
                          <p className="text-muted text-left">{member.blood_group || 'Not available'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-warning/10 rounded-lg flex-shrink-0">
                          <Award className="w-4 h-4 text-warning" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Access Level</span>
                          <div className="mt-1 text-left">
                            <StatusBadge status={member.access_level || 'Basic'} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                          <Shield className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Status</span>
                          <div className="mt-1 text-left">
                            <StatusBadge status={member.status || 'Unknown'} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleCard>

                {/* Contact Information - Left Aligned */}
                <CollapsibleCard
                  title="Contact Information"
                  isExpanded={expandedSections.contactInfo}
                  onToggle={() => toggleSection('contactInfo')}
                  icon={Phone}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                          <Phone className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Primary Contact</span>
                          {member.contact_no ? (
                            <a href={`tel:${member.contact_no}`} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline text-left">
                              {member.contact_no}
                            </a>
                          ) : (
                            <p className="text-muted">Not available</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-accent/15 rounded-lg flex-shrink-0">
                          <Mail className="w-4 h-4 text-accent" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Email Address</span>
                          <p className="text-muted break-words text-left">{member.email || 'Not available'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-warning/10 rounded-lg flex-shrink-0">
                          <Phone className="w-4 h-4 text-warning" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Alternative Contact</span>
                          {member.alternate_contact_no ? (
                            <a href={`tel:${member.alternate_contact_no}`} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline text-left">
                              {member.alternate_contact_no}
                            </a>
                          ) : (
                            <p className="text-muted">Not available</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-info/10 rounded-lg flex-shrink-0">
                          <Clock className="w-4 h-4 text-info" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block">Best Time to Contact</span>
                          <p className="text-muted text-left">{member.best_time_to_contact || 'Not available'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleCard>

                {/* Businesses - Left Aligned */}
                <CollapsibleCard
                  title={`Businesses (${businesses.length})`}
                  isExpanded={expandedSections.businesses}
                  onToggle={() => toggleSection('businesses')}
                  icon={Building2}
                >
                  {Array.isArray(businesses) && businesses.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {businesses.map((b) => (
                        <Card key={b.id} interactive padding="sm" className="transition-all duration-300">
                          <div className="flex flex-col gap-2 sm:gap-3">
                            <div className="flex-1 min-w-0 text-left">
                              <h4 className="font-display font-semibold text-ink mb-1 text-sm sm:text-base text-left">{b.company_name}</h4>
                              <p className="text-xs sm:text-sm text-muted mb-2 text-left">{b.business_type || b.designation || ''}</p>
                              {b.business_type && (
                                <Badge tone="primary">{b.business_type}</Badge>
                              )}
                            </div>
                            <div className="flex justify-start">
                              <Button as={Link} to={`/details/${b.id}`} size="sm">
                                View Details
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={<Building2 className="w-6 h-6" />}
                      title="No businesses listed"
                      description="This member hasn't added any business information yet."
                    />
                  )}
                </CollapsibleCard>
              </div>

              {/* Right Column - Sidebar - Left Aligned */}
              <div className="space-y-4 sm:space-y-6">
                {/* Family Information - Left Aligned */}
                <CollapsibleCard
                  title="Family Information"
                  isExpanded={expandedSections.familyInfo}
                  onToggle={() => toggleSection('familyInfo')}
                  icon={Users}
                >
                  {family ? (
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <h4 className="font-display font-semibold text-primary text-left border-b border-line/12 pb-2">Parents</h4>
                        {family.father_name && (
                          <div className="flex items-start gap-2">
                            <User className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Father: </span>
                              <span className="text-muted">{family.father_name}</span>
                            </div>
                          </div>
                        )}
                        {family.father_contact && (
                          <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Father Contact: </span>
                              <a href={`tel:${family.father_contact}`} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline text-left">
                                {family.father_contact}
                              </a>
                            </div>
                          </div>
                        )}
                        {family.mother_name && (
                          <div className="flex items-start gap-2">
                            <User className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Mother: </span>
                              <span className="text-muted">{family.mother_name}</span>
                            </div>
                          </div>
                        )}
                        {family.mother_contact && (
                          <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Mother Contact: </span>
                              <a href={`tel:${family.mother_contact}`} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline text-left">
                                {family.mother_contact}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-3 pt-4 border-t border-line/12">
                        <h4 className="font-display font-semibold text-primary text-left border-b border-line/12 pb-2">Spouse & Children</h4>
                        {family.spouse_name && (
                          <div className="flex items-start gap-2">
                            <Heart className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Spouse: </span>
                              <span className="text-muted">{family.spouse_name}</span>
                            </div>
                          </div>
                        )}
                        {family.spouse_contact && (
                          <div className="flex items-start gap-2">
                            <Phone className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Spouse Contact: </span>
                              <a href={`tel:${family.spouse_contact}`} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline text-left">
                                {family.spouse_contact}
                              </a>
                            </div>
                          </div>
                        )}
                        {typeof family.number_of_children === 'number' && (
                          <div className="flex items-start gap-2">
                            <Users className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Children: </span>
                              <span className="text-muted">{family.number_of_children}</span>
                            </div>
                          </div>
                        )}
                        {family.children_names && (
                          <div className="flex items-start gap-2">
                            <Users className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Children Names: </span>
                              <span className="text-muted">{formatChildrenNames(family.children_names)}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {family.address && (
                        <div className="pt-4 border-t border-line/12">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1 text-left">
                              <span className="font-medium text-ink">Family Address: </span>
                              <span className="text-muted">{family.address}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <EmptyState
                      icon={<Users className="w-6 h-6" />}
                      title="No family information available"
                      description="Family details haven't been added yet."
                    />
                  )}
                </CollapsibleCard>

                {/* Cultural Information - Left Aligned */}
                <CollapsibleCard
                  title="Cultural Information"
                  isExpanded={expandedSections.culturalInfo}
                  onToggle={() => toggleSection('culturalInfo')}
                  icon={Building2}
                >
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-warning/10 rounded-lg flex-shrink-0">
                        <Building2 className="w-3 h-3 sm:w-4 sm:h-4 text-warning" />
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <span className="font-semibold text-ink block text-sm sm:text-base">Kootam</span>
                        <p className="text-muted text-sm sm:text-base text-left">{member.kootam || 'Not available'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-accent/15 rounded-lg flex-shrink-0">
                        <Building2 className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
                      </div>
                      <div className="min-w-0 flex-1 text-left">
                        <span className="font-semibold text-ink block text-sm sm:text-base">Kovil</span>
                        <p className="text-muted text-sm sm:text-base text-left">{member.kovil || 'Not available'}</p>
                      </div>
                    </div>
                    {member.aadhar_no && (
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
                          <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <span className="font-semibold text-ink block text-sm sm:text-base">Aadhaar Number</span>
                          <p className="text-muted text-sm sm:text-base text-left">{member.aadhar_no}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleCard>
              </div>
            </div>
          </>
        )}
      </Container>

      <Footer />
      <MobileFooter />
    </div>
  );
};

export default MemberDetailsPage;
